// Iter 241 (Carlos #5): Buscador de Materiales — flujo de 2 fases.
// 1) Escribes → aparece un LISTADO de materiales sugeridos (solo nombre + N° compras).
// 2) Click en un material → se abre y muestra su historial: proveedores alternativos
//    con su último precio (más barato primero), estadísticas y tabla de compras.
import { useState, useEffect, useRef } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import {
  Search, Loader2, Package, Building2, TrendingUp, TrendingDown,
  ChevronDown, ChevronRight, Hash, History, BadgeCheck, Calendar,
} from 'lucide-react';
import { DocRefLink } from '../../components/DocRefLink';

const peso = (n) => '$' + Math.round(n || 0).toLocaleString('es-CL');
const fmtFecha = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

export default function BuscadorMateriales() {
  const [q, setQ] = useState('');
  const [sugerencias, setSugerencias] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const [detalles, setDetalles] = useState({}); // cache: key -> detalle
  const [loadingKey, setLoadingKey] = useState(null);
  const debRef = useRef(null);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (q.trim().length < 2) {
      setSugerencias([]); setTotal(0); setSearched(false); setOpenKey(null);
      return;
    }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/compras/materiales/sugerencias', { params: { q: q.trim(), limit: 40 } });
        setSugerencias(res.data.sugerencias || []);
        setTotal(res.data.total || 0);
        setSearched(true);
      } catch (e) {
        toast.error('Error buscando: ' + (e?.response?.data?.detail || e.message));
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [q]);

  const toggleMaterial = async (s) => {
    if (openKey === s.key) { setOpenKey(null); return; }
    setOpenKey(s.key);
    if (detalles[s.key]) return; // ya cacheado
    setLoadingKey(s.key);
    try {
      const res = await api.get('/compras/materiales/historial', { params: { key: s.key } });
      setDetalles((prev) => ({ ...prev, [s.key]: res.data }));
    } catch (e) {
      toast.error('Error cargando historial: ' + (e?.response?.data?.detail || e.message));
      setOpenKey(null);
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4" data-testid="buscador-materiales-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-amber-500/10 border border-amber-300 flex items-center justify-center shrink-0">
          <Package className="h-6 w-6 text-amber-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Buscador de Materiales</h1>
          <p className="text-sm text-gray-500">Escribe un material, selecciónalo de la lista y compara proveedores y precios.</p>
        </div>
      </div>

      {/* Search box */}
      <div className="relative">
        <Search className="absolute left-3.5 top-3.5 h-5 w-5 text-gray-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Escribe un material… (ej. perno hexagonal, disco corte, oxígeno)"
          className="pl-11 h-12 text-base shadow-sm"
          data-testid="buscador-materiales-input"
          autoFocus
        />
        {loading && <Loader2 className="absolute right-3.5 top-3.5 h-5 w-5 text-amber-500 animate-spin" />}
      </div>

      {searched && (
        <p className="text-xs text-gray-500" data-testid="buscador-materiales-count">
          {total === 0 ? 'Sin resultados' : `${total} material(es) — selecciona uno para ver su historial`}
        </p>
      )}

      {/* Placeholder */}
      {!searched && !loading && (
        <div className="text-center py-16 text-gray-400">
          <History className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Escribe el nombre de un material para ver las sugerencias.</p>
        </div>
      )}

      {/* LISTADO de sugerencias */}
      <div className="border rounded-xl bg-white shadow-sm divide-y overflow-hidden">
        {sugerencias.map((s, idx) => {
          const isOpen = openKey === s.key;
          const det = detalles[s.key];
          return (
            <div key={s.key} data-testid={`material-row-${idx}`}>
              {/* Fila del listado */}
              <button
                onClick={() => toggleMaterial(s)}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-amber-50/60 transition-colors ${isOpen ? 'bg-amber-50' : ''}`}
                data-testid={`material-toggle-${idx}`}
              >
                {isOpen ? <ChevronDown className="h-4 w-4 text-amber-600 shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
                <span className="flex-1 font-medium text-gray-800 truncate">{s.nombre || '(sin descripción)'}</span>
                {s.codigo && (
                  <span className="text-[10px] font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded items-center gap-1 hidden sm:inline-flex">
                    <Hash className="h-2.5 w-2.5" />{s.codigo}
                  </span>
                )}
                <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 shrink-0">{s.total_compras} compras</span>
              </button>

              {/* Detalle expandido */}
              {isOpen && (
                <div className="bg-gray-50/70 border-t px-4 py-4" data-testid={`material-detail-${idx}`}>
                  {loadingKey === s.key && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Cargando historial…</div>
                  )}
                  {det && (
                    <div className="space-y-4">
                      {/* Stats */}
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs bg-white border rounded-md px-2.5 py-1 flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 text-gray-400" /> Última: <strong>{fmtFecha(det.ultima_compra?.fecha)}</strong> · {peso(det.ultima_compra?.precio_unitario)}
                        </span>
                        <span className="text-xs bg-emerald-50 text-emerald-700 rounded-md px-2.5 py-1 flex items-center gap-1">
                          <TrendingDown className="h-3.5 w-3.5" /> mín {peso(det.precio_min)}
                        </span>
                        <span className="text-xs bg-rose-50 text-rose-700 rounded-md px-2.5 py-1 flex items-center gap-1">
                          <TrendingUp className="h-3.5 w-3.5" /> máx {peso(det.precio_max)}
                        </span>
                        <span className="text-xs bg-gray-100 text-gray-600 rounded-md px-2.5 py-1">prom {peso(det.precio_prom)}</span>
                      </div>

                      {/* Proveedores alternativos */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
                          <Building2 className="h-3.5 w-3.5" /> Proveedores ({det.proveedores?.length || 0}) — del más barato al más caro
                        </p>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {(det.proveedores || []).map((p, j) => (
                            <div key={j} className={`bg-white border rounded-lg p-2.5 flex items-center justify-between ${j === 0 ? 'border-emerald-300 ring-1 ring-emerald-200' : ''}`} data-testid={`material-prov-${idx}-${j}`}>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-700 truncate flex items-center gap-1">
                                  {j === 0 && p.ultimo_precio > 0 && <BadgeCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                                  {p.nombre}
                                </div>
                                <div className="text-[11px] text-gray-400">{p.compras} compra(s) · últ. {fmtFecha(p.ultima_fecha)}</div>
                              </div>
                              <div className="text-right shrink-0 ml-2">
                                <div className={`text-sm font-bold tabular-nums ${j === 0 && p.ultimo_precio > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>{peso(p.ultimo_precio)}</div>
                                <div className="text-[10px] text-gray-400">último precio</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Historial */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1"><History className="h-3.5 w-3.5" /> Historial ({det.total_compras})</p>
                        <div className="border rounded-lg overflow-x-auto bg-white max-h-80 overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0">
                              <tr className="bg-gray-100 text-gray-600 text-[11px]">
                                <th className="px-2 py-1.5 text-left">Fecha</th>
                                <th className="px-2 py-1.5 text-left">Proveedor</th>
                                <th className="px-2 py-1.5 text-right">Cant.</th>
                                <th className="px-2 py-1.5 text-center">Und</th>
                                <th className="px-2 py-1.5 text-right">V. Unit.</th>
                                <th className="px-2 py-1.5 text-right">Total</th>
                                <th className="px-2 py-1.5 text-center">OC</th>
                                <th className="px-2 py-1.5 text-center">OT</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(det.historial || []).map((h, hi) => (
                                <tr key={hi} className="border-t hover:bg-amber-50/40">
                                  <td className="px-2 py-1.5 whitespace-nowrap">{fmtFecha(h.fecha)}</td>
                                  <td className="px-2 py-1.5 max-w-[180px] truncate" title={h.proveedor}>{h.proveedor}</td>
                                  <td className="px-2 py-1.5 text-right font-mono">{h.cantidad}</td>
                                  <td className="px-2 py-1.5 text-center text-gray-500">{h.unidad || '—'}</td>
                                  <td className="px-2 py-1.5 text-right font-mono font-medium">{peso(h.precio_unitario)}</td>
                                  <td className="px-2 py-1.5 text-right font-mono text-gray-600">{peso(h.total)}</td>
                                  <td className="px-2 py-1.5 text-center font-mono">
                                    {h.numero_oc
                                      ? <DocRefLink tipo="oc" numero={h.numero_oc} label={h.numero_oc} testId={`material-oc-link-${idx}-${hi}`} />
                                      : <span className="text-gray-400">—</span>}
                                  </td>
                                  <td className="px-2 py-1.5 text-center text-gray-500">{h.ot_numero || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
